/* iter363 — Recipe Vault page (/dashboard/recipes).
 *
 * Shows all saved recipes (photo + URL imports) with metabolic scores
 * cached server-side. Filter chips: anti-inflammatory, low DIL, low NIS,
 * balanced ω, low sat-fat. Sort: newest, lowest UIS, lowest DIL, …
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChefHat, Flame, Activity, Fish, Droplet, Sparkles, ArrowLeft,
  Loader2, Heart, Trash2, Plus, Filter as FilterIcon, ListOrdered,
  Pencil, Save, X as XIcon,
} from "lucide-react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";

const FILTERS = [
  { id: "all",                bg: "Всички",                en: "All" },
  { id: "anti_inflammatory",  bg: "Противовъзпалителни",  en: "Anti-inflammatory" },
  { id: "low_dil",            bg: "Нисък DIL",             en: "Low DIL" },
  { id: "low_insulin",        bg: "Нисък NIS (инсулин)",  en: "Low NIS" },
  { id: "balanced_omega",     bg: "Балансирано ω",        en: "Balanced ω" },
  { id: "low_satfat",         bg: "Малко наситени мазн.", en: "Low sat-fat" },
];

const SORTS = [
  { id: "newest",    bg: "Най-нови",    en: "Newest" },
  { id: "uis_low",   bg: "Най-нисък UIS", en: "Lowest UIS" },
  { id: "dil_low",   bg: "Най-нисък DIL", en: "Lowest DIL" },
  { id: "nis_low",   bg: "Най-нисък NIS", en: "Lowest NIS" },
  { id: "omega_low", bg: "Най-добър ω",  en: "Best ω" },
];

function ScoreBadge({ label, value, unit = "", color, testid }) {
  if (value === null || value === undefined) return null;
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-start px-2 py-1 rounded-md text-[10px] font-semibold leading-tight"
      style={{ background: `${color}15`, color }}
    >
      <span className="opacity-70 text-[9px] uppercase tracking-wider">{label}</span>
      <span>{value}{unit}</span>
    </div>
  );
}

function uisColor(v) {
  if (v === null || v === undefined) return "#9CA3AF";
  if (v < 35) return "#10B981";
  if (v < 50) return "#84CC16";
  if (v < 65) return "#F59E0B";
  return "#EF4444";
}
function dilColor(v) {
  if (v === null || v === undefined) return "#9CA3AF";
  if (v < 30) return "#10B981";
  if (v < 50) return "#84CC16";
  if (v < 70) return "#F59E0B";
  return "#EF4444";
}
function nisColor(v) {
  if (v === null || v === undefined) return "#9CA3AF";
  if (v < 40) return "#10B981";
  if (v < 60) return "#84CC16";
  if (v < 75) return "#F59E0B";
  return "#EF4444";
}
function omegaColor(v) {
  if (v === null || v === undefined) return "#9CA3AF";
  if (v <= 4) return "#10B981";
  if (v <= 10) return "#F59E0B";
  return "#EF4444";
}

const MEAL_ICONS = { breakfast: "🌅", lunch: "🥗", dinner: "🍽️", snack: "🍎" };

export default function RecipeVaultPage() {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ recipes: [], total: 0, shown: 0 });
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [selected, setSelected] = useState(null);

  // iter595 — edit-mode state for the detail modal
  const [editing, setEditing] = useState(false);
  const [editIngs, setEditIngs] = useState([]);
  const [editMealType, setEditMealType] = useState("lunch");
  const [editName, setEditName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const beginEdit = (recipe) => {
    setEditing(true);
    setEditName(recipe.name || "");
    setEditMealType(recipe.meal_type || "lunch");
    setEditIngs((recipe.ingredients || []).map((i) => ({
      name: i.name || "",
      amount_g: Number(i.amount_g) || 100,
    })));
  };

  const cancelEdit = () => {
    setEditing(false);
    setEditIngs([]);
    setEditName("");
  };

  const saveEdit = async () => {
    if (!selected) return;
    const trimmedName = (editName || "").trim();
    if (!trimmedName) {
      toast.error(bg ? "Името е задължително" : "Name is required");
      return;
    }
    const cleaned = editIngs
      .map((i) => ({
        name: (i.name || "").trim(),
        amount_g: Math.max(1, Math.min(2000, Math.round(Number(i.amount_g) || 100))),
      }))
      .filter((i) => i.name.length > 0);
    if (cleaned.length === 0) {
      toast.error(bg ? "Поне една съставка е задължителна" : "At least one ingredient required");
      return;
    }
    setSavingEdit(true);
    try {
      const { data: payload } = await axios.put(
        `${API}/recipes/vault/${encodeURIComponent(selected.name)}`,
        { name: trimmedName, meal_type: editMealType, ingredients: cleaned },
      );
      toast.success(bg ? "Запазено ✓" : "Saved ✓");
      setEditing(false);
      // Refresh list and re-select the (possibly renamed) recipe
      await fetchVault();
      setSelected(payload?.recipe || { ...selected, name: trimmedName, ingredients: cleaned, meal_type: editMealType });
    } catch (err) {
      toast.error(err?.response?.data?.detail || (bg ? "Грешка при запис" : "Save failed"));
    } finally {
      setSavingEdit(false);
    }
  };

  // iter596 — taxonomy filters (parity with PublicCookbook)
  const [taxonomy, setTaxonomy] = useState({ dietary_patterns: [], metabolic_pathways: [] });
  const [dietaryFilter, setDietaryFilter] = useState("");
  const [pathwayFilter, setPathwayFilter] = useState("");
  const [mealFilter, setMealFilter] = useState("");
  const [showTaxFilters, setShowTaxFilters] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public-recipes/taxonomy`).then(({ data }) => {
      setTaxonomy({
        dietary_patterns: data?.dietary_patterns || [],
        metabolic_pathways: data?.metabolic_pathways || [],
      });
    }).catch(() => {});
  }, [API]);

  const fetchVault = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("filter", filter);
      if (sortBy !== "newest") params.set("sort", sortBy);
      if (dietaryFilter) params.set("dietary_pattern", dietaryFilter);
      if (pathwayFilter) params.set("metabolic_pathway", pathwayFilter);
      if (mealFilter) params.set("meal_type", mealFilter);
      const url = `${API}/recipes/vault${params.toString() ? "?" + params.toString() : ""}`;
      const { data } = await axios.get(url);
      setData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Error");
    } finally {
      setLoading(false);
    }
  }, [API, filter, sortBy, dietaryFilter, pathwayFilter, mealFilter]);

  useEffect(() => { fetchVault(); }, [fetchVault]);

  const deleteRecipe = async (name) => {
    if (!window.confirm(bg ? `Да изтрием ли „${name}“?` : `Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API}/recipes/favorite/${encodeURIComponent(name)}`);
      toast.success(bg ? "Изтрита" : "Deleted");
      setSelected(null);
      fetchVault();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const logToDiary = async (recipe) => {
    try {
      const { data: payload } = await axios.post(
        `${API}/recipes/vault/${encodeURIComponent(recipe.name)}/log`,
        {}
      );
      let ok = 0; const failed = [];
      for (const ing of payload.ingredients || []) {
        try {
          await axios.post(`${API}/meals/add`, {
            food_name: ing.name,
            amount_g: Math.max(1, Math.min(2000, Math.round(ing.amount_g || 100))),
            meal_type: payload.meal_type,
            calories: 0, protein: 0, carbs: 0, fat: 0,
            fiber: 0, sugar: 0, saturated_fat: 0,
          });
          ok++;
        } catch { failed.push(ing.name); }
      }
      if (ok) toast.success(bg ? `Добавени ${ok} съставки в дневника` : `Logged ${ok} ingredients`);
      if (failed.length) toast.error((bg ? "Не успях да добавя: " : "Failed: ") + failed.join(", "));
      setSelected(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-white/10 text-gray-100"
    : "bg-white border-gray-200 text-gray-900";

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,7%)]" : "bg-gradient-to-br from-fuchsia-50/30 via-white to-pink-50/30"}`}>
      <div className="max-w-6xl mx-auto px-4 py-6" data-testid="recipe-vault-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/dashboard" className={`p-2 -ml-2 rounded-lg hover:bg-black/5 ${isDark ? "hover:bg-white/5" : ""}`} data-testid="vault-back">
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-fuchsia-500" />
                <h1 className="text-xl font-bold">{bg ? "Готварска книга" : "Recipe Vault"}</h1>
              </div>
              <div className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg
                  ? "Всички запазени рецепти (от снимка, URL или дневник) с метаболитни scores"
                  : "All saved recipes (from photo, URL, or diary) with metabolic scores"}
              </div>
            </div>
          </div>
        </div>

        {/* Filter row */}
        <div className="mb-3 flex items-center gap-1.5 overflow-x-auto pb-2" data-testid="vault-filter-row">
          <FilterIcon className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-gray-400" : "text-gray-500"}`} />
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`vault-filter-${f.id}`}
              className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${
                filter === f.id
                  ? "bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white"
                  : isDark
                    ? "bg-white/5 text-gray-300 hover:bg-white/10"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {bg ? f.bg : f.en}
            </button>
          ))}
        </div>

        {/* Sort row */}
        <div className="mb-2 flex items-center gap-1.5 overflow-x-auto pb-2" data-testid="vault-sort-row">
          <ListOrdered className={`w-3.5 h-3.5 shrink-0 ${isDark ? "text-gray-400" : "text-gray-500"}`} />
          {SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSortBy(s.id)}
              data-testid={`vault-sort-${s.id}`}
              className={`shrink-0 px-2.5 py-0.5 rounded-md text-[10px] font-medium transition-colors ${
                sortBy === s.id
                  ? "bg-fuchsia-500/20 text-fuchsia-600 ring-1 ring-fuchsia-500/40"
                  : isDark
                    ? "text-gray-400 hover:text-gray-200"
                    : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {bg ? s.bg : s.en}
            </button>
          ))}
        </div>

        {/* iter596 — Taxonomy filters (parity with PublicCookbook) */}
        <div className="mb-4">
          <button
            onClick={() => setShowTaxFilters((v) => !v)}
            className={`text-[11px] font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md ${
              showTaxFilters || dietaryFilter || pathwayFilter || mealFilter
                ? "bg-fuchsia-500/15 text-fuchsia-500"
                : isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-600 hover:text-gray-900"
            }`}
            data-testid="vault-tax-filters-toggle"
          >
            🔍 {bg ? "Хранителен модел / Метаболитен път" : "Dietary / Pathway"}
            {(dietaryFilter || pathwayFilter || mealFilter) && (
              <span className="ml-1 px-1.5 rounded-full text-[9px] bg-fuchsia-500 text-white">
                {[dietaryFilter, pathwayFilter, mealFilter].filter(Boolean).length}
              </span>
            )}
          </button>
          {showTaxFilters && (
            <div className={`mt-2 p-2.5 rounded-lg border space-y-2 ${isDark ? "border-white/10 bg-white/5" : "border-gray-200 bg-gray-50"}`} data-testid="vault-tax-filters-panel">
              {/* Meal type chips */}
              <div>
                <div className={`text-[9px] font-bold uppercase tracking-wide mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {bg ? "Хранене" : "Meal"}
                </div>
                <div className="flex flex-wrap gap-1">
                  <VaultChip active={!mealFilter} onClick={() => setMealFilter("")} isDark={isDark} testid="vault-meal-chip-all">{bg ? "Всички" : "All"}</VaultChip>
                  {[
                    { id: "breakfast", bg: "Закуска",  en: "Breakfast", icon: "🌅" },
                    { id: "lunch",     bg: "Обяд",     en: "Lunch",     icon: "☀️" },
                    { id: "dinner",    bg: "Вечеря",   en: "Dinner",    icon: "🌙" },
                    { id: "snack",     bg: "Междинно", en: "Snack",     icon: "🍎" },
                  ].map(mt => (
                    <VaultChip
                      key={mt.id}
                      active={mealFilter === mt.id}
                      onClick={() => setMealFilter(mealFilter === mt.id ? "" : mt.id)}
                      isDark={isDark}
                      testid={`vault-meal-chip-${mt.id}`}
                    >{mt.icon} {bg ? mt.bg : mt.en}</VaultChip>
                  ))}
                </div>
              </div>
              {/* Dietary pattern chips */}
              <div>
                <div className={`text-[9px] font-bold uppercase tracking-wide mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {bg ? "Хранителен модел" : "Dietary pattern"}
                </div>
                <div className="flex flex-wrap gap-1">
                  <VaultChip active={!dietaryFilter} onClick={() => setDietaryFilter("")} isDark={isDark} testid="vault-dp-chip-all">{bg ? "Всички" : "All"}</VaultChip>
                  {taxonomy.dietary_patterns.map((dp) => (
                    <VaultChip
                      key={dp.id}
                      active={dietaryFilter === dp.id}
                      onClick={() => setDietaryFilter(dietaryFilter === dp.id ? "" : dp.id)}
                      isDark={isDark}
                      testid={`vault-dp-chip-${dp.id}`}
                    >{bg ? dp.label_bg : dp.label_en}</VaultChip>
                  ))}
                </div>
              </div>
              {/* Metabolic pathway chips */}
              <div>
                <div className={`text-[9px] font-bold uppercase tracking-wide mb-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  {bg ? "Метаболитен път" : "Metabolic pathway"}
                </div>
                <div className="flex flex-wrap gap-1">
                  <VaultChip active={!pathwayFilter} onClick={() => setPathwayFilter("")} isDark={isDark} testid="vault-mp-chip-all">{bg ? "Всички" : "All"}</VaultChip>
                  {taxonomy.metabolic_pathways.map((mp) => (
                    <VaultChip
                      key={mp.id}
                      active={pathwayFilter === mp.id}
                      onClick={() => setPathwayFilter(pathwayFilter === mp.id ? "" : mp.id)}
                      isDark={isDark}
                      testid={`vault-mp-chip-${mp.id}`}
                    >{bg ? mp.label_bg : mp.label_en}</VaultChip>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        {loading && (
          <div className="flex items-center justify-center py-20 gap-2 text-fuchsia-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">{bg ? "Зареждам…" : "Loading…"}</span>
          </div>
        )}

        {!loading && data.total === 0 && (
          <div className={`text-center py-16 rounded-2xl border ${cardCls}`} data-testid="vault-empty">
            <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <div className="text-base font-semibold mb-1">
              {bg ? "Все още нямате запазени рецепти" : "No saved recipes yet"}
            </div>
            <div className={`text-[12px] mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg
                ? "Запазете рецепти от снимка, URL или гласово въвеждане в дневника"
                : "Save recipes from photo, URL, or voice in the diary"}
            </div>
            <Link to="/dashboard/diary">
              <Button className="bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white">
                <Plus className="w-4 h-4 mr-1" /> {bg ? "Към дневника" : "Go to diary"}
              </Button>
            </Link>
          </div>
        )}

        {!loading && data.total > 0 && data.shown === 0 && (
          <div className={`text-center py-12 rounded-2xl border ${cardCls}`} data-testid="vault-no-results">
            <FilterIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <div className="text-sm">{bg ? "Никоя рецепта не отговаря на филтъра" : "No recipes match this filter"}</div>
          </div>
        )}

        {!loading && data.recipes.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vault-recipe-grid">
            {data.recipes.map((r) => {
              const s = r.scores || {};
              return (
                <button
                  key={r.name}
                  onClick={() => setSelected(r)}
                  data-testid={`vault-recipe-card-${r.name}`}
                  className={`text-left rounded-xl border overflow-hidden transition-all hover:shadow-lg hover:-translate-y-0.5 ${cardCls}`}
                >
                  {r.image_url && (
                    <div className="aspect-video bg-gray-200 overflow-hidden">
                      <img src={r.image_url} alt={r.name} className="w-full h-full object-cover"
                           onError={(e) => { e.target.style.display = "none"; }} />
                    </div>
                  )}
                  <div className="p-3">
                    <div className="flex items-start gap-2 mb-2">
                      <span className="text-base">{MEAL_ICONS[r.meal_type] || "🍴"}</span>
                      <div className="font-semibold text-sm leading-tight flex-1 line-clamp-2">{r.name}</div>
                    </div>
                    <div className={`text-[10px] mb-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {(r.ingredients || []).length} {bg ? "съставки" : "ingredients"}
                    </div>
                    <div className="grid grid-cols-4 gap-1">
                      <ScoreBadge label="UIS" value={s.uis_pct} unit="%" color={uisColor(s.uis_pct)} testid={`vault-score-uis-${r.name}`} />
                      <ScoreBadge label="DIL" value={s.dil} color={dilColor(s.dil)} testid={`vault-score-dil-${r.name}`} />
                      <ScoreBadge label="NIS" value={s.nis_pct} unit="%" color={nisColor(s.nis_pct)} testid={`vault-score-nis-${r.name}`} />
                      <ScoreBadge label="ω" value={s.omega_ratio !== null ? `${s.omega_ratio}:1` : null} color={omegaColor(s.omega_ratio)} testid={`vault-score-omega-${r.name}`} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {/* Detail modal */}
        {selected && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-3 bg-black/60 backdrop-blur-sm"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setSelected(null);
                cancelEdit();
              }
            }}
            data-testid="vault-detail-modal"
          >
            <div className={`w-full max-w-lg max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border ${cardCls}`}>
              <div className={`shrink-0 px-4 py-3 border-b ${isDark ? "border-white/10" : "border-gray-100"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">{MEAL_ICONS[editing ? editMealType : selected.meal_type] || "🍴"}</span>
                  {editing ? (
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className={`flex-1 min-w-0 text-base font-bold bg-transparent border-b focus:outline-none ${
                        isDark ? "border-white/20 focus:border-fuchsia-400" : "border-gray-300 focus:border-fuchsia-500"
                      }`}
                      data-testid="vault-edit-name"
                      placeholder={bg ? "Име на рецептата" : "Recipe name"}
                    />
                  ) : (
                    <h2 className="text-base font-bold flex-1 min-w-0 truncate">{selected.name}</h2>
                  )}
                  {!editing && (
                    <button
                      onClick={() => beginEdit(selected)}
                      className={`shrink-0 p-1.5 rounded-md hover:bg-fuchsia-500/10 ${isDark ? "text-fuchsia-300" : "text-fuchsia-600"}`}
                      title={bg ? "Редактиране" : "Edit"}
                      data-testid="vault-detail-edit-btn"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {editing && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {["breakfast", "lunch", "dinner", "snack"].map((mt) => (
                      <button
                        key={mt}
                        onClick={() => setEditMealType(mt)}
                        className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors ${
                          editMealType === mt
                            ? "bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white"
                            : isDark ? "bg-white/5 text-gray-300 hover:bg-white/10" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                        }`}
                        data-testid={`vault-edit-meal-${mt}`}
                      >
                        {bg
                          ? { breakfast: "Закуска", lunch: "Обяд", dinner: "Вечеря", snack: "Междинно" }[mt]
                          : mt.charAt(0).toUpperCase() + mt.slice(1)}
                      </button>
                    ))}
                  </div>
                )}

                {!editing && selected.scores && (
                  <div className="grid grid-cols-4 gap-1 mt-2">
                    <ScoreBadge label="UIS" value={selected.scores.uis_pct} unit="%" color={uisColor(selected.scores.uis_pct)} />
                    <ScoreBadge label="DIL" value={selected.scores.dil} color={dilColor(selected.scores.dil)} />
                    <ScoreBadge label="NIS" value={selected.scores.nis_pct} unit="%" color={nisColor(selected.scores.nis_pct)} />
                    <ScoreBadge label="ω" value={selected.scores.omega_ratio !== null ? `${selected.scores.omega_ratio}:1` : null} color={omegaColor(selected.scores.omega_ratio)} />
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3">
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 flex items-center justify-between ${isDark ? "text-fuchsia-300" : "text-fuchsia-600"}`}>
                  <span>{bg ? "Съставки" : "Ingredients"}</span>
                  {editing && (
                    <button
                      onClick={() => setEditIngs([...editIngs, { name: "", amount_g: 100 }])}
                      className={`text-[10px] inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${
                        isDark ? "bg-fuchsia-500/15 text-fuchsia-300 hover:bg-fuchsia-500/25" : "bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100"
                      }`}
                      data-testid="vault-edit-add-ingredient"
                    >
                      <Plus className="w-3 h-3" /> {bg ? "Добавяне" : "Add"}
                    </button>
                  )}
                </div>

                {editing ? (
                  <div className="space-y-1.5">
                    {editIngs.map((ing, i) => (
                      <div
                        key={i}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded ${isDark ? "bg-white/5" : "bg-gray-50"}`}
                        data-testid={`vault-edit-ingredient-row-${i}`}
                      >
                        <input
                          type="text"
                          value={ing.name}
                          onChange={(e) => {
                            const arr = [...editIngs];
                            arr[i] = { ...arr[i], name: e.target.value };
                            setEditIngs(arr);
                          }}
                          placeholder={bg ? "Съставка" : "Ingredient"}
                          className={`flex-1 min-w-0 text-[12px] bg-transparent border-b focus:outline-none ${
                            isDark ? "border-white/10 focus:border-fuchsia-400" : "border-gray-200 focus:border-fuchsia-500"
                          }`}
                          data-testid={`vault-edit-ingredient-name-${i}`}
                        />
                        <input
                          type="number"
                          min="1"
                          max="2000"
                          value={ing.amount_g}
                          onChange={(e) => {
                            const arr = [...editIngs];
                            arr[i] = { ...arr[i], amount_g: Number(e.target.value) };
                            setEditIngs(arr);
                          }}
                          className={`w-16 text-[12px] text-right bg-transparent border-b focus:outline-none ${
                            isDark ? "border-white/10 focus:border-fuchsia-400" : "border-gray-200 focus:border-fuchsia-500"
                          }`}
                          data-testid={`vault-edit-ingredient-amount-${i}`}
                        />
                        <span className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>g</span>
                        <button
                          type="button"
                          onClick={() => setEditIngs(editIngs.filter((_, idx) => idx !== i))}
                          className={`shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                            isDark ? "bg-red-500/15 hover:bg-red-500/30 text-red-300"
                                   : "bg-red-50 hover:bg-red-100 text-red-600 border border-red-200"
                          }`}
                          title={bg ? "Премахни" : "Remove"}
                          data-testid={`vault-edit-ingredient-remove-${i}`}
                          aria-label="remove ingredient"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {editIngs.length === 0 && (
                      <div className={`text-center text-[11px] py-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {bg ? "Натиснете „Добавяне“ за първата съставка" : "Press \"Add\" for the first ingredient"}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {(selected.ingredients || []).map((ing, i) => (
                      <div key={i} className={`flex items-center justify-between text-[12px] py-1 px-2 rounded ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                        <span className="font-medium">{ing.name}</span>
                        <span className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>{ing.amount_g}g</span>
                      </div>
                    ))}
                  </div>
                )}

                {!editing && selected.scores?.uis_tier_bg && (
                  <div className={`mt-3 text-[11px] italic ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {bg ? selected.scores.uis_tier_bg : selected.scores.uis_tier_en}
                  </div>
                )}
              </div>

              <div className={`shrink-0 px-3 py-2.5 flex items-center justify-between gap-2 border-t ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-gray-50"}`}>
                {editing ? (
                  <>
                    <Button variant="ghost" onClick={cancelEdit} disabled={savingEdit} data-testid="vault-edit-cancel">
                      <XIcon className="w-4 h-4 mr-1" /> {bg ? "Откажи" : "Cancel"}
                    </Button>
                    <Button
                      onClick={saveEdit}
                      disabled={savingEdit}
                      className="bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white"
                      data-testid="vault-edit-save"
                    >
                      {savingEdit ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                      {bg ? "Запазване" : "Save"}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" onClick={() => deleteRecipe(selected.name)} className="text-red-500" data-testid="vault-detail-delete">
                      <Trash2 className="w-4 h-4 mr-1" /> {bg ? "Изтриване" : "Delete"}
                    </Button>
                    <Button onClick={() => logToDiary(selected)} className="bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white" data-testid="vault-detail-log">
                      <Plus className="w-4 h-4 mr-1" /> {bg ? "Логни в дневника" : "Log to diary"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// iter596 — Pill-style chip helper (parity with PublicCookbook.FilterChip)
function VaultChip({ active, onClick, isDark, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all ${
        active
          ? "bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white shadow-sm"
          : isDark
            ? "bg-slate-800 text-gray-300 hover:bg-slate-700 border border-slate-700"
            : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
      }`}
    >
      {children}
    </button>
  );
}
